import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, X, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency } from '@/lib/utils'
import type { APIResponse, AppliedPromoState } from '@/types'

interface CounterPromoCodeBlockProps {
  orderId: string
  /**
   * Called on successful apply or remove so the parent can re-fetch the
   * order (totals will have changed). Failure cases (409) are surfaced
   * via toast in this component.
   */
  onChanged?: () => void
}

/**
 * Inline promo redemption surface for the counter. Rendered inside the
 * collapsible discount section of TenderInput so it sits flush with the
 * whole-bill discount field. Two states:
 *   - empty: code input + Apply button
 *   - applied: green badge with code + Remove
 *
 * Server-side mutex with manual discount is authoritative; this UI
 * surfaces a clear toast when the apply 409s with `conflict_*` reason.
 */
export function CounterPromoCodeBlock({ orderId, onChanged }: CounterPromoCodeBlockProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [code, setCode] = useState('')

  const promoStateQuery = useQuery<APIResponse<AppliedPromoState>>({
    queryKey: ['counter-applied-promo', orderId],
    queryFn: () => apiClient.getAppliedPromo(orderId),
    enabled: Boolean(orderId),
    staleTime: 5_000,
  })
  const applied = promoStateQuery.data?.data

  const applyMutation = useMutation({
    mutationFn: () => apiClient.applyPromoToOrder(orderId, code.trim()),
    onSuccess: (res) => {
      const data = res.data
      if (data) {
        toast({
          title: 'Promo applied',
          description: `${data.code} · ${formatCurrency(data.discount_applied)} off${data.applies_to ? ` ${data.applies_to}` : ''}`,
          variant: 'success',
        })
      }
      setCode('')
      queryClient.invalidateQueries({ queryKey: ['counter-applied-promo', orderId] })
      onChanged?.()
    },
    onError: (err) => {
      surfacePromoError(err, toast)
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => apiClient.removePromoFromOrder(orderId),
    onSuccess: () => {
      toast({ title: 'Promo removed', variant: 'success' })
      queryClient.invalidateQueries({ queryKey: ['counter-applied-promo', orderId] })
      onChanged?.()
    },
    onError: (err) => surfacePromoError(err, toast),
  })

  const busy = applyMutation.isPending || removeMutation.isPending || promoStateQuery.isLoading

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Promo code
      </div>
      {applied?.applied ? (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 dark:border-emerald-900/60 dark:bg-emerald-950/40">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            <Check className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 font-mono text-xs">
                {applied.code}
              </Badge>
              <span className="text-xs font-medium tabular-nums text-emerald-700 dark:text-emerald-300">
                −{formatCurrency(applied.discount_applied ?? 0)}
              </span>
            </div>
            {applied.applies_to ? (
              <div className="mt-0.5 truncate text-[11px] font-medium text-emerald-800 dark:text-emerald-200">
                On {applied.applies_to} only
              </div>
            ) : null}
            {applied.label ?? applied.description ? (
              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                {applied.label ?? applied.description}
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => removeMutation.mutate()}
            disabled={busy}
            className="h-8 gap-1 text-xs text-destructive hover:text-destructive"
          >
            {removeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
            Remove
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="BIRTHDAY25"
            maxLength={40}
            autoComplete="off"
            spellCheck={false}
            className="h-9 min-w-[6rem] flex-1 font-mono text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.trim().length >= 3 && !busy) {
                e.preventDefault()
                applyMutation.mutate()
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => applyMutation.mutate()}
            disabled={code.trim().length < 3 || busy}
            className="h-9 shrink-0 px-3 text-sm"
          >
            {applyMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      )}
    </div>
  )
}

function surfacePromoError(err: unknown, toast: ReturnType<typeof useToast>['toast']) {
  // F-DEAL-N3: best single discount wins — the server explains which deal
  // already saves more and by how much.
  if (err && typeof err === 'object' && (err as { code?: unknown }).code === 'better_auto_discount') {
    toast({ title: 'The deal already saves more', description: err instanceof Error ? err.message : '' })
    return
  }
  // Try to extract the structured `error` reason (set by stringPtr in
  // the Go handlers) so we can show a user-friendly message.
  const reason = extractErrorReason(err)
  const map: Record<string, { title: string; description: string }> = {
    code_unknown: { title: 'Code not found', description: 'Double-check the code with the customer.' },
    inactive: { title: 'Code is inactive', description: 'This code has been archived.' },
    expired: { title: 'Code is expired', description: 'This code is outside its validity window.' },
    max_redemptions: { title: 'Code limit reached', description: 'No more redemptions left on this code.' },
    per_customer_limit: { title: 'Already redeemed', description: 'This customer has already used this code.' },
    min_subtotal: { title: 'Order too small', description: 'The order (or, for an item-only code, its qualifying items) is below the minimum for this code.' },
    no_matching_items: { title: 'No qualifying items', description: 'This code only covers certain categories or items, and none are on this order.' },
    conflict_manual_discount: { title: 'Manual discount in place', description: 'Remove the manual discount before applying a promo.' },
    conflict_existing_promo: { title: 'Promo already applied', description: 'Remove the current promo before applying another.' },
    conflict_promo: { title: 'Promo already applied', description: 'Remove the promo before applying a manual discount.' },
    order_not_open: { title: 'Order is closed', description: 'Cannot edit a completed or cancelled order.' },
    no_customer_for_per_customer_cap: { title: 'Customer required', description: 'Attach customer details before applying this code.' },
  }
  const tx = (reason && map[reason]) || { title: 'Promo apply failed', description: err instanceof Error ? err.message : String(err) }
  toast({ title: tx.title, description: tx.description, variant: 'destructive' })
}

function extractErrorReason(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined
  // apiClient wraps failures in ApiClientError with the backend's snake_case
  // `error` on `.code`; the raw axios shape is kept as a fallback.
  const anyErr = err as { code?: unknown; response?: { data?: { error?: string } }; cause?: unknown }
  if (typeof anyErr.code === 'string' && /^[a-z_]+$/.test(anyErr.code)) return anyErr.code
  return anyErr.response?.data?.error
}
